import dayjs from 'dayjs';

import { DB_DATE_FORMAT } from 'core/constants';
import {
  ALL_CUSTOMERS_OPTION_ID,
  ALL_SALES_LOCATIONS_OPTION_ID,
} from 'quotes/components/ExportSkuPart/enums';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { IImportAttributesPayload, ISaveAttributesPayload } from 'quotes/types';

const formatTradeAgreementStartDate = (
  value: IAttributeForm['tradeAgreementStartDate']
): string | null => (value ? dayjs(value).format(DB_DATE_FORMAT) : null);

// The role-gated keys are sent only when the user may change them — see ISaveAttributesPayload.
interface IRoleGatedAttributeOptions {
  canChooseSpaStatus?: boolean;
  canEnableSelectableSpa?: boolean;
}

export const mapFormToImportAttributes = (
  data: IAttributeForm,
  { canChooseSpaStatus = false, canEnableSelectableSpa = false }: IRoleGatedAttributeOptions = {}
): IImportAttributesPayload => {
  const customerIds = data.allCustomers
    ? []
    : (data.customers ?? [])
        .map((customer) => customer.id)
        .filter((id) => id !== ALL_CUSTOMERS_OPTION_ID);

  return {
    attributeProjectId: data.allCustomers ? '' : data.project?.id ?? '',
    attributeCustomerIds: customerIds,
    attributeAllCustomers: data.allCustomers,
    attributeAllSalesLocations: data.allSalesLocations,
    attributeSalesLocationIds: data.allSalesLocations
      ? []
      : (data.salesLocations ?? [])
          .map((location) => location.id)
          .filter((id) => id !== ALL_SALES_LOCATIONS_OPTION_ID),
    attributeShippingLocationIds: (data.shippingLocations ?? []).map((location) => location.id),
    attributePriorityTier: data.spaPriorityTier,
    ...(canChooseSpaStatus && { attributeSpaStatus: data.spaStatus }),
    attributeTradeAgreementOwnerId: data.tradeAgreementOwner?.id ?? '',
    attributeTradeAgreementStartDate:
      formatTradeAgreementStartDate(data.tradeAgreementStartDate) ?? '',
    ...(canEnableSelectableSpa && { attributeSelectableSpaEnabled: !!data.selectableSpaEnabled }),
  };
};

/**
 * Every field is sent explicitly so the BE patch acts as a full overwrite — except
 * `selectableSpaEnabled` and `spaStatus`, omitted unless the user may change them (see
 * ISaveAttributesPayload).
 * A boolean reaches the wire as "true"/"false". Note the wire behavior in getFormData
 * (core/utils/formData.ts): a non-empty array serializes as indexed keys (`customer_ids[0]=...`),
 * while an empty array hits the scalar fall-through (line 25)
 * and is appended as a present empty-string value (`customer_ids=''`) — it is NOT omitted.
 * The BE reads that empty string as an explicit clear (AttributesService::normalizeIdList('')
 * returns [], persisted as NULL), so emptying a selection correctly clears the saved value.
 */
export const mapFormToSavePayload = (
  quoteId: number,
  data: IAttributeForm,
  { canChooseSpaStatus = false, canEnableSelectableSpa = false }: IRoleGatedAttributeOptions = {}
): ISaveAttributesPayload => {
  const customerIds = data.allCustomers
    ? []
    : (data.customers ?? [])
        .map((customer) => customer.id)
        .filter((id) => id !== ALL_CUSTOMERS_OPTION_ID);

  return {
    quoteId,
    projectId: data.allCustomers ? null : data.project?.id ?? null,
    customerIds,
    allCustomers: data.allCustomers,
    allSalesLocations: data.allSalesLocations,
    salesLocationIds: data.allSalesLocations
      ? []
      : (data.salesLocations ?? [])
          .map((location) => location.id)
          .filter((id) => id !== ALL_SALES_LOCATIONS_OPTION_ID),
    shippingLocationIds: (data.shippingLocations ?? []).map((location) => location.id),
    spaPriorityTier: data.spaPriorityTier ?? null,
    ...(canChooseSpaStatus && { spaStatus: data.spaStatus }),
    tradeAgreementOwnerId: data.tradeAgreementOwner?.id ?? null,
    tradeAgreementStartDate: formatTradeAgreementStartDate(data.tradeAgreementStartDate),
    ...(canEnableSelectableSpa && { selectableSpaEnabled: !!data.selectableSpaEnabled }),
  };
};
